import { DEFAULT_WIDGET_SPACING } from '../spacing';
import type { TextContentWidgetResolved } from '../title-widget/types';
import type { FrontendWidget } from '../types';
import { BookAppointmentWidget } from './BookAppointmentWidget';
import { BookAppointmentWidgetB } from './BookAppointmentWidgetB';
import { BookAppointmentWidgetC } from './BookAppointmentWidgetC';
import type { BookAppointmentConfig, BookAppointmentResolved } from './types';

// resolveNestedZones wraps component fields in an array even when repeatable=false;
// unwrap to the single object the frontend types expect.
const first = <T>(value: T | T[]): T =>
  Array.isArray(value) ? value[0] : value;

/**
 * Unwraps the nested heading and applies the same two defaults the standalone
 * Text Content Widget applies, so a heading reads identically whichever of the
 * two an editor authored it in. A heading with no kicker, title or body counts
 * as absent — the frontend would otherwise render an empty spacer block.
 */
function resolveHeading(
  raw: BookAppointmentConfig['heading'],
): TextContentWidgetResolved | undefined {
  const config = Array.isArray(raw) ? raw[0] : raw;
  if (!config) {
    return undefined;
  }
  const hasBody = Array.isArray(config.body) && config.body.length > 0;
  if (!config.kicker?.trim() && !config.title?.trim() && !hasBody) {
    return undefined;
  }
  return {
    ...config,
    alignment: config.alignment ?? 'center',
    spacing: config.spacing ?? DEFAULT_WIDGET_SPACING,
  };
}

type BookAppointmentRaw = Omit<
  BookAppointmentResolved,
  'stepDay' | 'stepTime' | 'stepForm' | 'pickerTexts' | 'heading'
> & {
  heading?: BookAppointmentConfig['heading'];
  stepDay:
    BookAppointmentResolved['stepDay'] | BookAppointmentResolved['stepDay'][];
  stepTime:
    BookAppointmentResolved['stepTime'] | BookAppointmentResolved['stepTime'][];
  stepForm:
    BookAppointmentResolved['stepForm'] | BookAppointmentResolved['stepForm'][];
  pickerTexts?:
    | BookAppointmentResolved['pickerTexts']
    | NonNullable<BookAppointmentResolved['pickerTexts']>[];
};

/** Spreadable: the key is absent rather than `undefined` when there is none. */
function headingOf(raw: BookAppointmentConfig['heading']): {
  heading?: TextContentWidgetResolved;
} {
  const heading = resolveHeading(raw);
  return heading === undefined ? {} : { heading };
}

export const bookAppointmentWidget: FrontendWidget<
  BookAppointmentRaw,
  BookAppointmentResolved
> = {
  componentType: 'widgets.book-appointment-widget',
  displayName: 'Book Appointment Widget',
  ComponentA: BookAppointmentWidget,
  ComponentB: BookAppointmentWidgetB,
  ComponentC: BookAppointmentWidgetC,
  resolveSync: ({ heading, ...config }) => ({
    ...config,
    // Pulled out of the spread: the raw `heading` is a component array, and
    // spreading it before the resolved one would leave the raw shape in place
    // whenever there is no heading to override it with.
    ...headingOf(heading),
    stepDay: first(config.stepDay),
    stepTime: first(config.stepTime),
    stepForm: first(config.stepForm),
    pickerTexts: config.pickerTexts ? first(config.pickerTexts) : undefined,
  }),
};
